import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, apiFetch } from "@/lib/api";
import type { SignedInUser } from "@/lib/api-types";
import { CURRENT_ACTOR_QUERY_KEY, useCurrentActor } from "@/lib/current-actor";

/**
 * Story 20.1 — extracted from `Settings.tsx`'s own `DarkModeToggle` (Story
 * 18.2) so the new nav-bar toggle can flip the same `theme_preference`
 * without a second, independent implementation (VC-20.1-02): one optimistic
 * `queryClient.setQueryData(CURRENT_ACTOR_QUERY_KEY, …)` write, one `PATCH
 * /auth/me/theme-preference` call site, rollback to the previous value on
 * failure. `error` is exposed so `Settings.tsx` can keep showing its own
 * inline failure message; the nav-bar toggle (an icon-only control with no
 * room for inline text) is free to ignore it.
 */
export function useThemePreference() {
  const { data: actor } = useCurrentActor();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);

  const isDark = actor?.theme_preference === "dark";

  async function toggle() {
    if (!actor) {
      return;
    }
    const next = actor.theme_preference === "dark" ? "light" : "dark";
    const previous = actor.theme_preference ?? null;
    setError(undefined);
    queryClient.setQueryData<SignedInUser>(CURRENT_ACTOR_QUERY_KEY, { ...actor, theme_preference: next });
    try {
      const updated = await apiFetch<SignedInUser>("/auth/me/theme-preference", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme_preference: next }),
      });
      queryClient.setQueryData<SignedInUser>(CURRENT_ACTOR_QUERY_KEY, updated);
    } catch (caught) {
      queryClient.setQueryData<SignedInUser>(CURRENT_ACTOR_QUERY_KEY, { ...actor, theme_preference: previous });
      setError(caught instanceof ApiError ? caught.message : "Something went wrong. Please try again in a moment.");
    }
  }

  return { actor, isDark, toggle, error };
}
